import React, { useEffect, useId, useState } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import FormControl, { FormControlProps } from '../FormControl'
import { Input, InputIcon, InputWrapper, VisibilityButton } from './styles'
import { AllColors } from '../../types/Colors'
import isIcon from '../../utils/isIcon'
import MaskedInput from 'react-input-mask'

const FALLBACK_PWD_REGEX = '^(?=.*[A-Za-z])(?=.*\\d)[\\w\\s !"#$%&\'()*+,-./:;<=>?@\\[\\]^_`{|}~]{8,}$'

export const PASSWORD_REGEX = new RegExp(window?.App?.pwdRegex || FALLBACK_PWD_REGEX)

export interface CustomIcon {
  icon: IconDefinition
  onClick?: () => void
  tooltipText?: string
  positionAtEnd?: boolean
  color?: AllColors
}

export interface TextFieldProps
  extends Omit<React.HTMLProps<HTMLInputElement>, 'label'>,
    Omit<FormControlProps, 'inputId' | 'children'> {
  placeholder?: string
  type?: string
  icon?: IconDefinition | CustomIcon
  testId?: string
  mask?: string
  maxLength?: number
  showCharsAmount?: boolean
  readonly?: boolean
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextFieldWithForwardedRefs(
  {
    label,
    id,
    as,
    required,
    error,
    placeholder,
    gutterBottom,
    fullWidth,
    type,
    icon,
    hint,
    testId,
    hiddenLabel,
    disabled,
    labelIcon,
    hintBox,
    className,
    mask,
    tabIndex,
    tooltip,
    tooltipPlacement,
    maxLength,
    showCharsAmount,
    onChange,
    value,
    readonly,
    ...otherProps
  },
  ref,
) {
  const [showPassword, setShowPassword] = useState(false)
  const [charsAmount, setCharsAmount] = useState(0)
  const generatedId = useId()
  const fieldId = id || generatedId

  useEffect(() => {
    if (showCharsAmount) setCharsAmount(`${value}`.length)
  }, [value])

  const isPassword = type === 'password'
  const passwordInputType = showPassword ? 'text' : 'password'
  const inputType = isPassword ? passwordInputType : type

  const parsedIconComponent: CustomIcon = isIcon(icon) ? { icon } : icon

  const iconPosition: ('start' | 'end')[] = parsedIconComponent?.positionAtEnd || isPassword ? ['end'] : []
  if (!!parsedIconComponent && !parsedIconComponent?.positionAtEnd) iconPosition.push('start')

  return (
    <FormControl
      inputId={fieldId}
      label={label}
      required={required}
      error={error}
      fullWidth={fullWidth}
      gutterBottom={gutterBottom}
      hint={hint}
      hiddenLabel={hiddenLabel}
      disabled={disabled}
      hintBox={hintBox}
      labelIcon={labelIcon}
      className={className}
      tooltip={tooltip}
      tooltipPlacement={tooltipPlacement}
    >
      <InputWrapper
        className="input-wrapper"
        error={!!error}
        disabled={disabled}
        iconPosition={iconPosition}
        showCharsAmount={showCharsAmount}
      >
        {!!icon && (
          <InputIcon
            className="input-icon"
            icon={parsedIconComponent.icon}
            color={parsedIconComponent?.color}
            onClick={parsedIconComponent?.onClick}
            positionAtEnd={parsedIconComponent?.positionAtEnd}
            tooltipText={parsedIconComponent?.tooltipText}
          />
        )}
        <Input
          {...otherProps}
          value={value}
          onChange={onChange}
          disabled={disabled || readonly}
          as={mask ? (MaskedInput as unknown as typeof Input) : null}
          mask={mask}
          tabIndex={tabIndex}
          type={inputType}
          id={fieldId}
          ref={ref}
          data-testid={testId && `${testId}-text-input`}
          placeholder={placeholder}
          maxLength={maxLength}
          className="input-textfield"
        />

        {isPassword && (
          <VisibilityButton
            disabled={disabled}
            onClick={() => setShowPassword(!showPassword)}
            showPassword={showPassword}
          />
        )}

        {showCharsAmount && !!charsAmount && (
          <span className="chars-amount">{`${charsAmount}${maxLength ? `/${maxLength}` : ''}`}</span>
        )}
      </InputWrapper>
    </FormControl>
  )
})

export default TextField
